import { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useCursorPagination } from '@frameio/graphql-client';
import type { ProjectOrderInput } from '@frameio/graphql-client/generated';
import { SearchFieldWithI18n } from '@frameio/shared/components/SearchField';
import { useProjectSortBy } from '@frameio/shared/hooks';
import { DEFAULT_PROJECT_FILTERS } from '@frameio/shared/resources/constants';
import { mapSortByToProjectOrderInputs } from '@frameio/shared/utils';
import {
  DrillDownScrollingColumn,
  NavGroup,
  NavItem,
  ScrollContainer,
  Spinner,
  Text,
  theme,
  useSheetInitialFocusRef,
} from '@frameio/vapor';
import { IconProject } from '@frameio/vapor-icons';
import { useGetMoveToCopyToProjectsQuery } from '../gql/generated/GetMoveToCopyToProjects';
import styles from './AccountProjectsView.module.css';
import TEXT from './generated/TEXT/AccountProjectsView';
import { ProjectRow } from './ProjectRow';
import { RecentProjects } from './RecentProjects';
import type {
  MoveToCopyToProject,
  MoveToCopyToWorkspace,
  WorkspaceProjectSelectCallback,
} from './types';

TEXT.DEFINE({
  /** Placeholder text for project search */
  searchPlaceholder: 'Find project',
  /** Label for the recent projects group */
  recent: 'Recent',
  /** Empty state message when no projects match search */
  searchEmptyState: 'No projects matching',
  /** Empty state query text for search */
  searchEmptyStateQuery: (query: string) => `"${query}"`,
  /** Button label to load more projects */
  loadMore: (count: number) => `+${count} More`,
  /** Aria label for the spinner */
  spinnerAriaLabel: 'Spinner',
  /** Aria label for the load more spinner */
  loadMoreSpinnerLabel: 'Loading more projects',
});

type AccountProjectsViewProps = {
  accountId: string;
  isShowing: boolean;
  onWorkspaceProjectSelect: WorkspaceProjectSelectCallback;
};

const WORKSPACE_NAME_ORDER: ProjectOrderInput = {
  field: 'WORKSPACE_NAME',
  direction: 'ASC',
};

const WORKSPACE_PAGE_SIZE = 100;

export const AccountProjectsView = ({
  accountId,
  isShowing,
  onWorkspaceProjectSelect,
}: AccountProjectsViewProps) => {
  const focusRef = useSheetInitialFocusRef();
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy] = useProjectSortBy();

  const { data, previousData, loading, fetchNext } = useCursorPagination(
    useGetMoveToCopyToProjectsQuery({
      variables: {
        accountId,
        page: { first: WORKSPACE_PAGE_SIZE },
        projectStatus: DEFAULT_PROJECT_FILTERS,
        query,
        orders: [WORKSPACE_NAME_ORDER, mapSortByToProjectOrderInputs(sortBy)],
      },
      skip: !accountId || !isShowing,
    })
  );

  const projects = data?.account?.projects;
  const pageInfo = projects?.pageInfo;
  const totalCount = projects?.totalCount ?? 0;
  const remotePending = loading || inputValue !== query;
  const sourceNodes = projects?.nodes ?? previousData?.account?.projects?.nodes;

  const displayNodes = useMemo(() => {
    if (remotePending && inputValue) {
      const needle = inputValue.toLowerCase();
      return (sourceNodes ?? []).filter((project) =>
        project.name.toLowerCase().includes(needle)
      );
    }
    return sourceNodes ?? [];
  }, [remotePending, inputValue, sourceNodes]);

  /**
   * Projects come back ordered by workspace name, so a workspace's projects are
   * contiguous and can be grouped by walking the list once.
   */
  const groups = useMemo(() => {
    const result: {
      workspace: MoveToCopyToWorkspace;
      projects: MoveToCopyToProject[];
    }[] = [];

    for (const project of displayNodes) {
      const { workspace } = project;
      if (workspace) {
        const currentGroup = result[result.length - 1];
        if (currentGroup?.workspace.id === workspace.id) {
          currentGroup.projects.push(project);
        } else {
          result.push({ workspace, projects: [project] });
        }
      }
    }

    return result;
  }, [displayNodes]);

  const isInitialLoading = loading && !data && !previousData;
  const showSearchEmptyState =
    Boolean(inputValue) &&
    !loading &&
    inputValue === query &&
    groups.length === 0;
  const howManyMore = totalCount - (projects?.nodes?.length ?? 0);
  const canLoadMore =
    !remotePending && Boolean(pageInfo?.hasNextPage) && howManyMore > 0;

  const handleLoadMore = useCallback(() => {
    if (!pageInfo || isLoadingMore) {
      return;
    }

    const result = fetchNext({ count: WORKSPACE_PAGE_SIZE, pageInfo });
    if (!result) {
      return;
    }

    setIsLoadingMore(true);

    result.finally(() => setIsLoadingMore(false));
  }, [pageInfo, isLoadingMore, fetchNext]);

  const initialLoadingState = useMemo(
    () => (
      <div className={styles.centeredOverlay}>
        <Spinner
          aria-label={TEXT.spinnerAriaLabel}
          delay={100}
          isLoading={true}
        />
      </div>
    ),
    []
  );

  const projectList = useMemo(() => {
    if (showSearchEmptyState) {
      return (
        <div className={styles.emptyState}>
          <Text color="tertiary" variant="body">
            {TEXT.searchEmptyState}
          </Text>
          <Text variant="body">{TEXT.searchEmptyStateQuery(query)}</Text>
        </div>
      );
    }

    return (
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={FADE_IN_TRANSITION}
      >
        {!inputValue && (
          <RecentProjects
            label={TEXT.recent}
            onWorkspaceProjectSelect={onWorkspaceProjectSelect}
          />
        )}
        {groups.map(({ workspace, projects: workspaceProjects }) => (
          <NavGroup key={workspace.id} label={workspace.name}>
            {workspaceProjects.map((project) => (
              <ProjectRow
                key={project.id}
                onSelect={() =>
                  onWorkspaceProjectSelect({ project, workspace })
                }
                projectId={project.id}
                query={inputValue}
              />
            ))}
          </NavGroup>
        ))}
        {isLoadingMore && (
          <div className={styles.loadMoreSpinner}>
            <Spinner
              aria-label={TEXT.loadMoreSpinnerLabel}
              delay={SPINNER_DELAY}
              isLoading={true}
              size="x-small"
            />
          </div>
        )}
        {!isLoadingMore && canLoadMore && (
          <NavGroup>
            <NavItem
              as="button"
              iconBefore={<IconProject />}
              isSelected={false}
              label={TEXT.loadMore(howManyMore)}
              onClick={handleLoadMore}
              variant="secondary"
            />
          </NavGroup>
        )}
      </motion.div>
    );
  }, [
    showSearchEmptyState,
    query,
    inputValue,
    groups,
    isLoadingMore,
    canLoadMore,
    howManyMore,
    handleLoadMore,
    onWorkspaceProjectSelect,
  ]);

  return (
    <DrillDownScrollingColumn enableTopBorder={false}>
      <ScrollContainer
        height="100%"
        overflow="hidden"
        paddingX={SCROLL_CONTAINER_PADDING_X}
      >
        <div className={styles.searchContainer}>
          <SearchFieldWithI18n
            ref={focusRef}
            aria-autocomplete="list"
            autoFocus={true}
            debounce={SEARCH_DEBOUNCE}
            isSearching={loading}
            onQueryChange={setInputValue}
            onQueryClear={() => setInputValue('')}
            onRequestSearch={setQuery}
            placeholder={TEXT.searchPlaceholder}
          />
        </div>
        <div className={styles.content}>
          {isInitialLoading ? initialLoadingState : projectList}
        </div>
      </ScrollContainer>
    </DrillDownScrollingColumn>
  );
};

const SEARCH_DEBOUNCE = 500;
const SCROLL_CONTAINER_PADDING_X = 16;
const SPINNER_DELAY = 100;
const FADE_IN_TRANSITION = {
  duration: 0.25,
  ease: theme.transitions.curves.easeOut,
};
